import { execFileSync } from 'node:child_process';

import { isLocalDev } from './env.ts';
import { error, hint, info, success, warn } from './output.ts';
import { pkg } from './package-root.ts';

const PACKAGE_NAME = '@sortlist/cli';

function getCurrentVersion(): string {
  return pkg.version;
}

async function fetchLatestVersion(): Promise<string | null> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch(`https://registry.npmjs.org/${encodeURIComponent(PACKAGE_NAME)}/latest`, {
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const data = (await res.json()) as { version: string };
    return data.version;
  } catch {
    return null;
  }
}

function parseSemver(version: string): [number, number, number] {
  const [major, minor, patch] = version.split('.').map(Number);
  return [major ?? 0, minor ?? 0, patch ?? 0];
}

function autoUpdate(current: string, latest: string): void {
  info(`Updating ${PACKAGE_NAME}: ${current} \u2192 ${latest}...`);

  try {
    // Clean stale temp directories that cause ENOTEMPTY errors. npm stages a
    // package under a sibling `.<name>-<hash>` dir; for scoped names the dot
    // sits on the final segment, e.g. `@sortlist/.cli-<hash>`.
    const prefix = execFileSync('npm', ['prefix', '-g'], { stdio: 'pipe', encoding: 'utf-8' }).trim();
    const lastSlash = PACKAGE_NAME.lastIndexOf('/');
    const scope = lastSlash === -1 ? '' : `${PACKAGE_NAME.slice(0, lastSlash)}/`;
    const name = lastSlash === -1 ? PACKAGE_NAME : PACKAGE_NAME.slice(lastSlash + 1);
    const staleDir = `${prefix}/lib/node_modules/${scope}.${name}-`;
    execFileSync('sh', ['-c', `rm -rf "${staleDir}"* 2>/dev/null || true`], { stdio: 'pipe' });

    execFileSync('npm', ['install', '-g', '--prefer-online', `${PACKAGE_NAME}@${latest}`], {
      stdio: 'pipe',
    });
    success(`Updated to ${latest}. Restart \`sl\` to use the new version.`);
  } catch (err) {
    const stderr = err && typeof err === 'object' && 'stderr' in err ? String(err.stderr).trim() : '';
    const detail = stderr ? ` (${stderr.split('\n')[0]})` : '';
    warn(`Auto-update failed${detail}.`);
    hint(`Run \`npm i -g ${PACKAGE_NAME}\` to update manually.`);
  }
}

/**
 * Checks npm for the latest version.
 * - Major mismatch: blocks execution and exits.
 * - Minor/patch mismatch: auto-updates with a spinner, then continues.
 */
export async function checkForUpdates(): Promise<void> {
  if (isLocalDev) return;

  const current = getCurrentVersion();
  const latest = await fetchLatestVersion();
  if (!latest || latest === current) return;

  const [curMajor] = parseSemver(current);
  const [latMajor] = parseSemver(latest);

  if (latMajor > curMajor) {
    error(`Breaking update: ${current} \u2192 ${latest}`);
    info('');
    info('  Your CLI is incompatible with the current API.');
    info('  Update before continuing:');
    info('');
    info(`  npm i -g ${PACKAGE_NAME}`);
    info('');
    process.exit(1);
  }

  autoUpdate(current, latest);
}
